import React, { createContext, useState, useRef, useEffect } from 'react';
import SoundFXManager from './SoundFXManager';

export const SoundContext = createContext({
  isMuted: false,
  toggleMute: () => {},
  filterVolume: 50,
  setFilterVolume: () => {},
  pumpVolume: 50,
  setPumpVolume: () => {},
  soundFxVolume: 50,
  setSoundFxVolume: () => {},
  playSoundFx: () => {},
  setMute: () => {},
  preloadSfxPhase: () => {},
  preloadLoops: () => {},
});



// Loop file paths (static require)
const FILTER_LOOP = require('../Assets/Audio/Loops/filter-trickling-v2.ogg');
const PUMP_LOOP = require('../Assets/Audio/Loops/pump-bubbling-v2.ogg');

export const SoundProvider = ({ children }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [filterVolume, setFilterVolume] = useState(50);
  const [pumpVolume, setPumpVolume] = useState(50);
  const [soundFxVolume, setSoundFxVolume] = useState(50);
  const soundFxVolumeRef = useRef(soundFxVolume);
  useEffect(() => { soundFxVolumeRef.current = soundFxVolume; }, [soundFxVolume]);

  // Store loop buffers for preloading
  const filterBufferRef = useRef(null);
  const pumpBufferRef = useRef(null);
  // Preload background loops (filter and pump) and store in refs
  const preloadLoops = async () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    // Helper to fetch and decode
    async function loadBuffer(url) {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      return await ctx.decodeAudioData(arrayBuffer);
    }
    // Only load if not already loaded
    if (!filterBufferRef.current) {
      try {
        filterBufferRef.current = await loadBuffer(FILTER_LOOP);
      } catch (e) {
  // LATER: handle error in production (failed to preload filter loop)
      }
    }
    if (!pumpBufferRef.current) {
      try {
        pumpBufferRef.current = await loadBuffer(PUMP_LOOP);
      } catch (e) {
  // LATER: handle error in production (failed to preload pump loop)
      }
    }
  };

  // Store previous volumes for restoration on unmute
  const prevVolumesRef = useRef({
    filter: 50,
    pump: 50,
    fx: 50
  });

  // Setup Web Audio API context and SoundFXManager
  const audioCtxRef = useRef(null);
  const sfxManagerRef = useRef(null);

  // Track which SFX phase is loaded
  const sfxPhaseRef = useRef('none'); // 'none', 'intro', 'kill'

  // Helper to ensure AudioContext is resumed
  const ensureAudioContextResumed = async () => {
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      try {
        await audioCtxRef.current.resume();
      } catch (error) {
  // LATER: handle error in production (failed to resume AudioContext)
      }
    }
  };

  useEffect(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (!sfxManagerRef.current) {
      // Pass a getter function that always reads from the ref
      sfxManagerRef.current = new SoundFXManager(
        audioCtxRef.current,
        () => soundFxVolumeRef.current
      );
    }
    // Always update mute state on manager
    sfxManagerRef.current.setMuted(isMuted);
    // No need to update volume, getter is always used

    // Pre-warm AudioContext on first user interaction to eliminate delays
    const preWarmAudioContext = async () => {
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        try {
          await audioCtxRef.current.resume();
          // ...removed debug log for production...
        } catch (error) {
          // LATER: handle error in production (failed to pre-warm AudioContext)
        }
      }
    };

    // Listen for any user interaction to pre-warm the context
    const interactionEvents = ['click', 'keydown', 'touchstart'];
    const handleFirstInteraction = () => {
      preWarmAudioContext();
      // Remove listeners after first interaction
      interactionEvents.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
    };

    // Always add listeners for pre-warming (AudioContext starts suspended)
    interactionEvents.forEach(event => {
      document.addEventListener(event, handleFirstInteraction, { once: true });
    });

    return () => {
      // Cleanup listeners if component unmounts before interaction
      interactionEvents.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
    };
  }, [isMuted]);

  // Phased SFX loading
  const preloadSfxPhase = async (phase) => {
    if (!sfxManagerRef.current) return;
    // Ensure AudioContext is resumed before preloading
    await ensureAudioContextResumed();
    
    if (phase === 'intro' && sfxPhaseRef.current !== 'intro') {
      // Intro phase loads all Vibe SFX immediately (optimal for immediate readiness)
      await sfxManagerRef.current.preload(SoundFXManager.getIntroSfxKeys());
      sfxPhaseRef.current = 'intro';
      // Since intro = vibe SFX, mark vibe as loaded too
      sfxPhaseRef.current = 'vibe';
    } else if (phase === 'vibe' && sfxPhaseRef.current !== 'vibe') {
      // Vibe SFX should already be loaded from intro, but load if needed
      await sfxManagerRef.current.preload(SoundFXManager.getIntroSfxKeys());
      sfxPhaseRef.current = 'vibe';
    } else if (phase === 'kill' && sfxPhaseRef.current !== 'kill') {
      // Always load intro SFX first if not already loaded
      if (sfxPhaseRef.current !== 'intro' && sfxPhaseRef.current !== 'vibe') {
        await sfxManagerRef.current.preload(SoundFXManager.getIntroSfxKeys());
      }
      await sfxManagerRef.current.preload(SoundFXManager.getKillSfxKeys());
      sfxPhaseRef.current = 'kill';
    }
  };

  // Mute: store current, set all to 0. Unmute: restore previous.
  const setMute = (mute) => {
    if (mute) {
      prevVolumesRef.current = {
        filter: filterVolume,
        pump: pumpVolume,
        fx: soundFxVolume
      };
      setFilterVolume(0);
      setPumpVolume(0);
      setSoundFxVolume(0);
      setIsMuted(true);
    } else {
      setFilterVolume(prevVolumesRef.current.filter);
      setPumpVolume(prevVolumesRef.current.pump);
      setSoundFxVolume(prevVolumesRef.current.fx);
      setIsMuted(false);
    }
    // Also update SFX manager mute state
    if (sfxManagerRef.current) {
      sfxManagerRef.current.setMuted(mute);
    }
  };

  const toggleMute = () => setMute(!isMuted);

  // If user drags a slider while muted, unmute and set only that slider, others stay zero
  const setFilterVolumeWithUnmute = (val) => {
    if (isMuted && val > 0) {
      setIsMuted(false);
      setFilterVolume(val);
      setPumpVolume(0);
      setSoundFxVolume(0);
    } else {
      setFilterVolume(val);
    }
  };
  const setPumpVolumeWithUnmute = (val) => {
    if (isMuted && val > 0) {
      setIsMuted(false);
      setPumpVolume(val);
      setFilterVolume(0);
      setSoundFxVolume(0);
    } else {
      setPumpVolume(val);
    }
  };
  const setSoundFxVolumeWithUnmute = (val, cb) => {
    if (isMuted && val > 0) {
      setIsMuted(false);
      setSoundFxVolume(val);
      setFilterVolume(0);
      setPumpVolume(0);
      if (typeof cb === 'function') setTimeout(cb, 0);
    } else {
      setSoundFxVolume(val);
      if (typeof cb === 'function') setTimeout(cb, 0);
    }
  };


  // Play SFX by name
  const playSoundFx = async (name, opts) => {
    if (sfxManagerRef.current) {
      // Ensure AudioContext is resumed before playing
      await ensureAudioContextResumed();
      sfxManagerRef.current.playSoundFx(name, opts);
    }
  };


  return (
    <SoundContext.Provider value={{
      isMuted,
      toggleMute,
      filterVolume,
      setFilterVolume: setFilterVolumeWithUnmute,
      pumpVolume,
      setPumpVolume: setPumpVolumeWithUnmute,
      soundFxVolume,
      setSoundFxVolume: setSoundFxVolumeWithUnmute,
      playSoundFx,
      setMute, // expose for advanced use
      preloadSfxPhase,
      ensureAudioContextResumed, // expose for modal buttons and other UI interactions
      preloadLoops, // expose for early loop preloading
      filterBuffer: filterBufferRef,
      pumpBuffer: pumpBufferRef,
      audioContext: audioCtxRef,
    }}>
      {children}
    </SoundContext.Provider>
  );
};
